import { useEffect, useRef, type RefObject } from "react";

const SCRIPT_SRC = "https://open.video/video.js";

declare global {
  interface Window {
    openVideoPlayers?: {
      float: boolean;
      autoplay: boolean;
      allowPlaylistAds: boolean;
      target: HTMLElement;
      videoID: string;
    }[];
  }
}

/**
 * Mount an Open.Video player into `ref`.
 *
 * Open.Video's script reads a queue off `window.openVideoPlayers` when it
 * loads. That is fine on a full page load, which is all the Manus build ever
 * did: Home and the old /auth page each pushed one config and let the script
 * pick it up. It is not fine inside a modal: once the script has run, pushing a
 * new config does nothing, so a player that mounts and unmounts with the dialog
 * comes back blank the second time it is opened.
 *
 * Callers therefore have to keep the container mounted for the life of the
 * page (see AuthModal, which uses forceMount for exactly this reason). This
 * hook enforces the other half of the contract: it initialises once and only
 * once per container, so React StrictMode's double-invoked effects in
 * development cannot queue the same player twice.
 *
 * Note the default of `autoplay: false`. The old /auth page autoplayed, which
 * is a reasonable thing to do on a page someone navigated to deliberately and a
 * poor one in a dialog that opens over what they were reading.
 *
 * `enabled` defers everything, including loading Open.Video's script at all,
 * until the caller says so. AuthModal uses it so a visitor who never opens the
 * dialog never pays for a third-party script.
 */
export function useOpenVideoPlayer(
  ref: RefObject<HTMLElement | null>,
  videoID: string,
  options: { autoplay?: boolean; enabled?: boolean } = {}
): void {
  const { autoplay = false, enabled = true } = options;
  const initialised = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (initialised.current) return;
    const target = ref.current;
    if (!target) return;
    initialised.current = true;

    window.openVideoPlayers = window.openVideoPlayers || [];
    window.openVideoPlayers.push({
      float: false,
      autoplay,
      allowPlaylistAds: false,
      target,
      videoID,
    });

    if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.setAttribute("data-cfasync", "false");
      document.body.appendChild(script);
    }
  }, [ref, videoID, autoplay, enabled]);
}
